import { acceptGDPR, getSurvey } from '../actions/surveyActions';
import InformationLayout from '../components/InformationLayout';
import { breakpoints, colors, fonts } from '../styles';
import { globalColor } from '../utils/globals';
import { authenticatedGetJson } from '../utils/requests';
import useViewport from '../utils/useViewport';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

interface Reportee {
    name: string;
    status: number;
    userId: string;
}

const STATUS_NOT_STARTED = 1;
const STATUS_IN_PROGRESS = 2;
const STATUS_COMPLETED = 3;

const LEFT_SIZE = 65;

const PageContent = styled.div`
    display: flex;
    width: 100% !important;
    ${breakpoints.onlyMobile} {
        flex-direction: column;
    }
`;

const LeftPanel = styled.div`
    width: ${LEFT_SIZE}%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    box-sizing: border-box;
    padding-right: 30px;
    ${breakpoints.onlyMobile} {
        width: 100%;
        padding-right: 0;
    }
`;

const RightPanel = styled.div`
    width: ${100 - LEFT_SIZE}%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    ${breakpoints.onlyMobile} {
        display: none;
    }
`;

const Title = styled.div`
    ${fonts.title};
    color: ${() => globalColor.titleColor};
    text-transform: uppercase;
    margin-bottom: 24px;
`;

const Tagline = styled.div`
    ${fonts.subtitle};
    color: ${colors.textColor1};
    font-family: 'Open Sans', sans-serif;
    font-weight: bold;
`;

const DescriptionText = styled.p`
    font-family: 'Open Sans', sans-serif;
    font-weight: 600;
    font-size: 20px;
    color: #3a404d;
    line-height: 1.5;
    margin: 0 0 24px 0;
`;

const SearchWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 300px;
    margin-bottom: 16px;
    ${breakpoints.onlyMobile} {
        max-width: 100%;
    }
`;

const SearchInput = styled.input`
    padding: 8px 32px 8px 12px;
    font-size: 14px;
    border: 1px solid #d8d8d8;
    border-radius: 4px;
    width: 100%;
    box-sizing: border-box;
    font-family: 'Inter', sans-serif;
    &:focus-visible {
        outline: none;
        border-color: #b8a467;
    }
    &::-webkit-search-cancel-button {
        display: none;
        -webkit-appearance: none;
    }
`;

const ClearButton = styled.button`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    color: #999;
    padding: 0 4px;
    line-height: 1;
`;

const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 90%;

    ${breakpoints.onlyMobile} {
        width: 100%;
    }
`;

const TableHeader = styled.div<{ scrollbarWidth?: number }>`
    display: flex;
    align-items: center;
    background-color: #f0f0f0;
    border: 1px solid #d8d8d8;
    min-height: 41px;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #3a404d;
    padding-right: ${(props) => props.scrollbarWidth || 0}px;
    ${breakpoints.onlyMobile} {
        font-size: 12px;
        min-height: 36px;
        padding-right: 0;
    }
`;

const HeaderCell = styled.div<{ flex: number; textAlign?: string }>`
    flex: ${(props) => props.flex};
    padding: 0 23px;
    text-align: ${(props) => props.textAlign || 'left'};
    min-width: 0;
    ${breakpoints.onlyMobile} {
        padding: 0 10px;
    }
`;

const TableRow = styled.div`
    display: flex;
    align-items: center;
    background-color: #fff;
    border: 1px solid #d8d8d8;
    min-height: 47px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #3a404d;
    ${breakpoints.onlyMobile} {
        font-size: 12px;
        min-height: 42px;
    }
`;

const RowCell = styled.div<{ flex: number; textAlign?: string }>`
    flex: ${(props) => props.flex};
    padding: 9px 23px;
    text-align: ${(props) => props.textAlign || 'left'};
    min-width: 0;
    word-break: break-word;
    ${breakpoints.onlyMobile} {
        padding: 8px 10px;
    }
`;

const ActionButton = styled.button<{
    variant: 'proceed' | 'continue' | 'review';
}>`
    width: 83px;
    padding: 6px 10px;
    font-size: 12px;
    border-radius: 6px;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    ${breakpoints.onlyMobile} {
        width: 70px;
        font-size: 11px;
        padding: 5px 6px;
    }

    ${(props) => {
        switch (props.variant) {
            case 'proceed':
                return `
                    background-color: #32c77f;
                    color: #fff;
                    border: none;
                `;
            case 'continue':
                return `
                    background-color: #e2ad1d;
                    color: #fff;
                    border: none;
                `;
            case 'review':
                return `
                    background-color: #fff;
                    color: #32c77f;
                    border: 1px solid #32c77f;
                `;
        }
    }}

    &:hover {
        opacity: 0.85;
    }
`;

const ActionCellContent = styled.div`
    display: flex;
    justify-content: center;
`;

const ScrollableTableBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: calc(100vh - 480px);
    overflow-y: auto;
    ${breakpoints.onlyMobile} {
        max-height: unset;
    }
`;

const NoResultsMessage = styled.div`
    padding: 24px;
    text-align: center;
    color: #999;
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    border: 1px solid #d8d8d8;
    background-color: #fff;
`;

const MiddleImage = styled.img`
    max-width: 100%;
    max-height: 100%;
    width: 100%;
`;

const getStatusLabel = (
    status: number,
    labels: Record<string, string>,
): string => {
    switch (status) {
        case STATUS_NOT_STARTED:
            return labels.notStarted;
        case STATUS_IN_PROGRESS:
            return labels.inProgress;
        case STATUS_COMPLETED:
            return labels.completed;
        default:
            return '';
    }
};

const getActionVariant = (
    status: number,
): 'proceed' | 'continue' | 'review' => {
    switch (status) {
        case STATUS_IN_PROGRESS:
            return 'continue';
        case STATUS_COMPLETED:
            return 'review';
        default:
            return 'proceed';
    }
};

const getActionLabel = (
    status: number,
    labels: Record<string, string>,
): string => {
    switch (status) {
        case STATUS_IN_PROGRESS:
            return labels.continue;
        case STATUS_COMPLETED:
            return labels.review;
        default:
            return labels.proceed;
    }
};

const ReporteesListPage: React.FC = (): React.ReactElement => {
    const { t } = useTranslation(['ReporteesList']);
    const { survey_id, uid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { width } = useViewport();

    const [reportees, setReportees] = useState<Reportee[]>([]);
    const [search, setSearch] = useState('');
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const tableBodyRef = useRef<HTMLDivElement>(null);

    const checkScrollbar = useCallback(() => {
        const el = tableBodyRef.current;
        if (el) {
            setScrollbarWidth(el.offsetWidth - el.clientWidth);
        }
    }, []);

    let emailAuth: any;
    const stateVar = location.state;
    if (!stateVar || !stateVar.emailAuth) {
        const storedEmailAuth = sessionStorage.getItem('emailAuth');
        if (storedEmailAuth) {
            emailAuth = storedEmailAuth;
        }
    } else {
        emailAuth = stateVar.emailAuth;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    useEffect(() => {
        async function fetchReportees() {
            const response = await authenticatedGetJson(
                `${backendUrl}/manager_reportees`,
                survey_id,
                uid,
            );
            if (Array.isArray(response)) {
                setReportees(response as Reportee[]);
            }
        }
        fetchReportees();
    }, [location.key]);

    const statusLabels = t('statusLabels', {
        returnObjects: true,
    }) as Record<string, string>;
    const actionLabels = t('actionLabels', {
        returnObjects: true,
    }) as Record<string, string>;
    const columns = t('columns', {
        returnObjects: true,
    }) as Record<string, string>;

    const filteredData = search
        ? reportees.filter((r) =>
              r.name.toLowerCase().includes(search.toLowerCase()),
          )
        : reportees;

    useEffect(() => {
        checkScrollbar();
        const el = tableBodyRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => checkScrollbar());
        observer.observe(el);
        return () => observer.disconnect();
    }, [filteredData.length, checkScrollbar]);

    const handleClearSearch = () => {
        setSearch('');
    };

    const handleAction = async (reportee: Reportee) => {
        sessionStorage.setItem('managerUid', uid || '');
        sessionStorage.setItem('selectedReporteeName', reportee.name);
        sessionStorage.setItem('isManagerFlow', 'true');
        if (emailAuth) {
            sessionStorage.setItem('emailAuth', emailAuth);
        }
        await dispatch(acceptGDPR(survey_id, reportee.userId));
        await dispatch(getSurvey(survey_id, reportee.userId));
        navigate(`/${survey_id}/${reportee.userId}/more-info`, {
            state: {
                emailAuth: emailAuth,
            },
        });
    };

    return (
        <InformationLayout
            leftSize={LEFT_SIZE}
            canOverflow={true}
            logoImage="/talentbuilderlogo.png"
        >
            <PageContent>
                <LeftPanel>
                    <Title>{t('title')}</Title>
                    {/* <Tagline>{t('tagline')}</Tagline> */}
                    <DescriptionText>{t('description')}</DescriptionText>
                    <SearchWrapper>
                        <SearchInput
                            type="text"
                            placeholder={t('searchPlaceholder') as string}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <ClearButton
                                onClick={handleClearSearch}
                                aria-label="Clear search"
                            >
                                &#x2715;
                            </ClearButton>
                        )}
                    </SearchWrapper>
                    <TableWrapper>
                        <TableHeader scrollbarWidth={scrollbarWidth}>
                            <HeaderCell flex={3}>{columns.name}</HeaderCell>
                            <HeaderCell flex={2}>{columns.status}</HeaderCell>
                            <HeaderCell flex={1} textAlign="center">
                                {columns.action}
                            </HeaderCell>
                        </TableHeader>
                        {filteredData.length > 0 ? (
                            <ScrollableTableBody ref={tableBodyRef}>
                                {filteredData.map((reportee, index) => (
                                    <TableRow
                                        key={`${reportee.userId}-${index}`}
                                    >
                                        <RowCell flex={3}>
                                            {reportee.name}
                                        </RowCell>
                                        <RowCell flex={2}>
                                            {getStatusLabel(
                                                reportee.status,
                                                statusLabels,
                                            )}
                                        </RowCell>
                                        <RowCell flex={1}>
                                            <ActionCellContent>
                                                <ActionButton
                                                    variant={getActionVariant(
                                                        reportee.status,
                                                    )}
                                                    onClick={() =>
                                                        handleAction(reportee)
                                                    }
                                                >
                                                    {getActionLabel(
                                                        reportee.status,
                                                        actionLabels,
                                                    )}
                                                </ActionButton>
                                            </ActionCellContent>
                                        </RowCell>
                                    </TableRow>
                                ))}
                            </ScrollableTableBody>
                        ) : (
                            <NoResultsMessage>
                                {t('noResults', {
                                    defaultValue: 'No results found',
                                })}
                            </NoResultsMessage>
                        )}
                    </TableWrapper>
                </LeftPanel>
                {width > breakpoints.value.mobile && (
                    <RightPanel>
                        <MiddleImage src={t('middleImage') || ''} />
                    </RightPanel>
                )}
            </PageContent>
        </InformationLayout>
    );
};

export default ReporteesListPage;
